/**
 * Shared notification-category presentation helpers.
 *
 * The notification-type registry (GET /api/email/notification-types) carries a
 * `category` per type. These helpers turn that into a consistent, grouped display
 * so the Email Settings page and the Email Recipients dialog stay in lockstep
 * (single source of truth — no per-page drift).
 */

// Human-friendly labels per category, in the order they should be displayed.
export const CATEGORY_LABELS = {
  approvals: 'Approvals & Verifications',
  lifecycle: 'Contract Lifecycle',
  reminders: 'Scheduled Reminders (internal digests)',
  payments: 'Payments & Money Movement',
  portal: 'Client Portal & Onboarding',
  other: 'Other',
};

// Stable display order; any category not listed falls back after these (then 'other').
const CATEGORY_ORDER = ['approvals', 'lifecycle', 'reminders', 'payments', 'portal', 'other'];

/**
 * Group notification types by category into an ordered array.
 * @param {Array<{key:string,label:string,category?:string}>} types
 * @returns {Array<{ category: string, label: string, items: Array }>}
 */
export const groupByCategory = (types = []) => {
  const buckets = {};
  for (const t of types) {
    const cat = t.category || 'other';
    (buckets[cat] = buckets[cat] || []).push(t);
  }
  // Known categories first (in CATEGORY_ORDER), then any unknown ones alphabetically.
  const known = CATEGORY_ORDER.filter(c => buckets[c]);
  const unknown = Object.keys(buckets).filter(c => !CATEGORY_ORDER.includes(c)).sort();
  return [...known, ...unknown].map(category => ({
    category,
    label: CATEGORY_LABELS[category] || category,
    items: buckets[category],
  }));
};

/**
 * Approval-REQUEST notification keys ('submitted for approval'). These are ONE
 * user-facing concept split across two backend types (contract_approval +
 * change_submitted_for_approval), both `recipient: 'approvers'`. They are linked:
 * subscribing/un-subscribing one applies to all, so the grid shows a single toggle.
 * Derived from the fetched registry metadata — mirrors the backend
 * notificationTypes.APPROVAL_REQUEST_KEYS so the two never drift.
 * @param {Array<{key:string,recipient?:string}>} types
 * @returns {string[]}
 */
export const approvalRequestKeys = (types = []) =>
  types.filter(n => n.recipient === 'approvers').map(n => n.key);

/**
 * The set of preference keys that move together when toggling `key`. For an
 * approval-request type that is the whole linked group; for anything else just [key].
 * @param {Array<{key:string,recipient?:string}>} types
 * @param {string} key
 * @returns {string[]}
 */
export const linkedSuppressionKeys = (types = [], key) => {
  const group = approvalRequestKeys(types);
  return group.includes(key) ? group : [key];
};
